import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trophy, TrendingUp, TrendingDown, Wrench, Phone, Users, BarChart3, Clock, ChevronDown, ChevronUp, Filter, Download, FileSpreadsheet, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERIODS = [
  { value: 'today', label: 'Bu gün' },
  { value: 'yesterday', label: 'Dünən' },
  { value: 'last_7_days', label: 'Son 7 gün' },
  { value: 'last_30_days', label: 'Son 30 gün' },
  { value: 'current_month', label: 'Bu ay' },
  { value: 'last_month', label: 'Keçən ay' },
];

const REPORT_TYPES = {
  tickets: {
    title: 'Servis Hesabatı',
    description: 'Bütün servislər - tarix, texnik, qrup, status, müddət, operator qeydi',
    icon: Wrench, color: 'indigo'
  },
  connections: {
    title: 'Qoşulma Hesabatı',
    description: 'Bütün qoşulmalar - müştəri, tarif, texnik, status, müqavilə nömrəsi',
    icon: Phone, color: 'emerald'
  },
  performance: {
    title: 'Texnik Performans Hesabatı',
    description: 'Qrup üzrə texniklərin xülasəsi - xal, servis, qoşulma, D2D, orta vaxt',
    icon: Trophy, color: 'purple'
  },
  d2d: {
    title: 'D2D Hesabatı',
    description: 'D2D qeydləri - agent, müştəri, status, çevrilmə',
    icon: Users, color: 'orange'
  }
};

export default function TechnicianPerformancePanel({ user }) {
  const [data, setData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('current_month');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [expandedTech, setExpandedTech] = useState(null);
  const [sortBy, setSortBy] = useState('total_score');
  const [downloadDialog, setDownloadDialog] = useState(null); // {type, format}

  const token = localStorage.getItem('token');

  const fetchData = async () => {
    setLoading(true);
    try {
      let url = `${API}/technicians/performance?period=${period}`;
      let trendUrl = `${API}/technicians/performance/trends?months=6`;
      if (selectedGroup && selectedGroup !== 'all') {
        url += `&group_id=${selectedGroup}`;
        trendUrl += `&group_id=${selectedGroup}`;
      }
      const [perfRes, trendRes] = await Promise.all([
        axios.get(url, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(trendUrl, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { months: [] } }))
      ]);
      setData(perfRes.data);
      setTrendData(trendRes.data);
    } catch (error) {
      console.error('Performance data error:', error);
      toast.error('Performans məlumatları yüklənmədi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [period, selectedGroup]);

  const sortedTechnicians = useMemo(() => {
    if (!data?.technicians) return [];
    return [...data.technicians].sort((a, b) => {
      if (sortBy === 'total_score') return b.total_score - a.total_score;
      if (sortBy === 'tickets_closed') return b.tickets_closed - a.tickets_closed;
      if (sortBy === 'connections_completed') return b.connections_completed - a.connections_completed;
      if (sortBy === 'd2d_converted') return b.d2d_converted - a.d2d_converted;
      if (sortBy === 'tickets_avg_hours') return a.tickets_avg_hours - b.tickets_avg_hours;
      return 0;
    });
  }, [data, sortBy]);

  const handleExportExcel = async (type) => {
    try {
      let url = `${API}/reports/${type}/excel?period=${period}`;
      if (selectedGroup && selectedGroup !== 'all') {
        url += `&group_id=${selectedGroup}`;
      }
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${type}_${period || 'all'}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(blobUrl);
      toast.success('Hesabat yükləndi');
      setDownloadDialog(null);
    } catch (error) {
      toast.error('Hesabat yüklənmədi');
    }
  };

  const handleExportCSV = () => {
    if (!sortedTechnicians.length) return;
    const headers = 'Texnik,Qruplar,Ümumi Xal,Servislər (Bağlanmış),Servislər (Geri Qayt.),Orta Saat,Qoşulmalar (Tamamlanmış),Qoşulmalar (Geri Qayt.),D2D (Cəm),D2D (Çevrilmiş)';
    const rows = sortedTechnicians.map(t =>
      `"${t.technician_full_name}","${t.group_names?.join(', ')}",${t.total_score},${t.tickets_closed},${t.tickets_rejected},${t.tickets_avg_hours},${t.connections_completed},${t.connections_rejected},${t.d2d_total},${t.d2d_converted}`
    );
    const csv = '\ufeff' + headers + '\n' + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `texnik_performans_${period}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setDownloadDialog(null);
    toast.success('CSV yükləndi');
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-zinc-600">Yüklənir...</span>
      </div>
    );
  }

  const summary = data?.summary || {};
  const groups = data?.groups || [];

  return (
    <div className="space-y-6" data-testid="technician-performance-panel">
      {/* Filterlər */}
      <div className="bg-white rounded-lg border border-zinc-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-zinc-500" />
            <span className="text-sm font-medium text-zinc-700">Filterlər:</span>
          </div>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[160px]" data-testid="period-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map(p => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {groups.length > 1 && (
            <Select value={selectedGroup} onValueChange={setSelectedGroup}>
              <SelectTrigger className="w-[200px]" data-testid="group-filter">
                <SelectValue placeholder="Bütün qruplar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Bütün qruplar</SelectItem>
                {groups.map(g => (
                  <SelectItem key={g.group_id} value={g.group_id}>{g.group_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[180px]" data-testid="sort-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="total_score">Ümumi xala görə</SelectItem>
              <SelectItem value="tickets_closed">Servislərə görə</SelectItem>
              <SelectItem value="connections_completed">Qoşulmalara görə</SelectItem>
              <SelectItem value="d2d_converted">D2D-yə görə</SelectItem>
              <SelectItem value="tickets_avg_hours">Sürətə görə</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => setDownloadDialog({ type: 'csv', format: 'csv' })} data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-1" />
            CSV
          </Button>
          <div className="flex items-center gap-1 border-l pl-2 ml-1">
            <span className="text-xs text-zinc-500 mr-1">Excel:</span>
            <Button variant="outline" size="sm" onClick={() => setDownloadDialog({ type: 'tickets', format: 'excel' })} data-testid="export-tickets-excel" title="Servis hesabatı">
              <FileSpreadsheet className="h-4 w-4 mr-1" />
              Servis
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDownloadDialog({ type: 'connections', format: 'excel' })} data-testid="export-connections-excel" title="Qoşulma hesabatı">
              <FileSpreadsheet className="h-4 w-4 mr-1" />
              Qoşulma
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDownloadDialog({ type: 'performance', format: 'excel' })} data-testid="export-performance-excel" title="Texnik performans hesabatı">
              <FileSpreadsheet className="h-4 w-4 mr-1" />
              Performans
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDownloadDialog({ type: 'd2d', format: 'excel' })} data-testid="export-d2d-excel" title="D2D hesabatı">
              <FileSpreadsheet className="h-4 w-4 mr-1" />
              D2D
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="refresh-btn">
            {loading ? '...' : 'Yenilə'}
          </Button>
        </div>
      </div>

      {/* Xülasə Kartları */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <SummaryCard icon={Users} label="Texniklər" value={summary.total_technicians || 0} color="blue" />
        <SummaryCard icon={Trophy} label="Ümumi xal" value={summary.total_score || 0} color={summary.total_score >= 0 ? 'green' : 'red'} />
        <SummaryCard icon={Wrench} label="Servislər" value={summary.total_tickets_closed || 0} color="indigo" />
        <SummaryCard icon={Phone} label="Qoşulmalar" value={summary.total_connections_completed || 0} color="emerald" />
        <SummaryCard icon={Users} label="D2D Çevrilmiş" value={summary.total_d2d_converted || 0} color="orange" />
        <SummaryCard icon={Clock} label="Ort. saat (Servis)" value={`${summary.avg_ticket_hours || 0}s`} color="amber" />
      </div>

      {/* Aylıq Trend Qrafiki */}
      {trendData?.months?.length > 1 && (
        <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-700 to-indigo-700 px-4 py-3">
            <h3 className="text-sm font-bold text-white uppercase tracking-wide flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Son {trendData.months.length} Ay Trend
            </h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Bar Chart - Ümumi */}
              <div>
                <p className="text-xs font-semibold text-zinc-500 uppercase mb-2">Ümumi tamamlama</p>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={trendData.months} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="tickets_closed" name="Servislər" fill="#6366f1" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="connections_completed" name="Qoşulmalar" fill="#10b981" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="d2d_converted" name="D2D" fill="#f97316" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* Line Chart - Trend */}
              <div>
                <p className="text-xs font-semibold text-zinc-500 uppercase mb-2">Aylıq trend</p>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={trendData.months} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="total" name="Cəm" stroke="#2563eb" strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="tickets_closed" name="Servislər" stroke="#6366f1" strokeWidth={1.5} strokeDasharray="5 5" />
                    <Line type="monotone" dataKey="connections_completed" name="Qoşulmalar" stroke="#10b981" strokeWidth={1.5} strokeDasharray="5 5" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Qrup Xülasəsi */}
      {groups.length > 1 && selectedGroup === 'all' && (
        <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
          <div className="bg-zinc-800 px-4 py-3">
            <h3 className="text-sm font-bold text-white uppercase tracking-wide">Qrup Xülasəsi</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-zinc-700">Qrup</th>
                  <th className="px-4 py-2 text-center font-semibold text-zinc-700">Texnik</th>
                  <th className="px-4 py-2 text-center font-semibold text-zinc-700">Xal</th>
                  <th className="px-4 py-2 text-center font-semibold text-zinc-700">Servislər</th>
                  <th className="px-4 py-2 text-center font-semibold text-zinc-700">Qoşulmalar</th>
                  <th className="px-4 py-2 text-center font-semibold text-zinc-700">D2D</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {groups.map(g => (
                  <tr key={g.group_id} className="hover:bg-zinc-50 cursor-pointer" onClick={() => setSelectedGroup(g.group_id)}>
                    <td className="px-4 py-2 font-medium text-zinc-900">{g.group_name}</td>
                    <td className="px-4 py-2 text-center text-zinc-600">{g.technician_count}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`font-bold ${g.total_score >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {g.total_score > 0 ? '+' : ''}{g.total_score}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center text-indigo-600 font-medium">{g.tickets_closed}</td>
                    <td className="px-4 py-2 text-center text-emerald-600 font-medium">{g.connections_completed}</td>
                    <td className="px-4 py-2 text-center text-orange-600 font-medium">{g.d2d_converted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Texnik Siyahısı */}
      <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
        <div className="bg-blue-700 px-4 py-3 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white uppercase tracking-wide flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Texnik Performans ({sortedTechnicians.length})
          </h3>
        </div>

        <div className="divide-y divide-zinc-100">
          {sortedTechnicians.length === 0 ? (
            <div className="p-8 text-center text-zinc-500">Bu dövr üçün məlumat yoxdur</div>
          ) : (
            sortedTechnicians.map((tech, index) => (
              <TechnicianRow
                key={tech.technician_id}
                tech={tech}
                rank={index + 1}
                expanded={expandedTech === tech.technician_id}
                onToggle={() => setExpandedTech(expandedTech === tech.technician_id ? null : tech.technician_id)}
              />
            ))
          )}
        </div>
      </div>

      {/* Download Təsdiq Dialoqu */}
      <DownloadConfirmDialog
        downloadDialog={downloadDialog}
        onClose={() => setDownloadDialog(null)}
        onConfirm={() => {
          if (!downloadDialog) return;
          if (downloadDialog.format === 'csv') handleExportCSV();
          else handleExportExcel(downloadDialog.type);
        }}
        period={period}
        selectedGroup={selectedGroup}
        groups={groups}
        summary={summary}
      />
    </div>
  );
}

function DownloadConfirmDialog({ downloadDialog, onClose, onConfirm, period, selectedGroup, groups, summary }) {
  if (!downloadDialog) return null;
  const isCSV = downloadDialog.format === 'csv';
  const reportInfo = isCSV
    ? { title: 'Texnik Performans (CSV)', description: 'Ekrandakı texnik performans cədvəli CSV formatında', icon: Download, color: 'blue' }
    : REPORT_TYPES[downloadDialog.type] || {};

  const Icon = reportInfo.icon || FileSpreadsheet;
  const periodLabel = PERIODS.find(p => p.value === period)?.label || period;
  const groupLabel = selectedGroup === 'all'
    ? 'Bütün qruplar'
    : groups?.find(g => g.group_id === selectedGroup)?.group_name || selectedGroup;

  const colorBg = {
    indigo: 'bg-indigo-100 text-indigo-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    purple: 'bg-purple-100 text-purple-700',
    orange: 'bg-orange-100 text-orange-700',
    blue: 'bg-blue-100 text-blue-700',
  };

  return (
    <Dialog open={!!downloadDialog} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="download-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <div className={`p-2 rounded-lg ${colorBg[reportInfo.color] || colorBg.blue}`}>
              <Icon className="h-5 w-5" />
            </div>
            Hesabat Yüklə
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="bg-zinc-50 rounded-lg p-3 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">Hesabat növü</span>
              <span className="font-semibold text-zinc-900">{reportInfo.title}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">Format</span>
              <span className="font-semibold text-zinc-900">{isCSV ? 'CSV' : 'Excel (.xlsx)'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">Dövr</span>
              <span className="font-semibold text-zinc-900">{periodLabel}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">Qrup</span>
              <span className="font-semibold text-zinc-900">{groupLabel}</span>
            </div>
            {summary?.total_technicians > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">Texnik sayı</span>
                <span className="font-semibold text-zinc-900">{summary.total_technicians}</span>
              </div>
            )}
          </div>

          <p className="text-xs text-zinc-500 leading-relaxed">
            {reportInfo.description}
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="download-cancel-btn">Ləğv et</Button>
          <Button onClick={onConfirm} className="bg-blue-600 hover:bg-blue-700" data-testid="download-confirm-btn">
            <Download className="h-4 w-4 mr-2" />
            Yüklə
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryCard({ icon: Icon, label, value, color }) {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  return (
    <div className={`rounded-lg border p-3 ${colorMap[color] || colorMap.blue}`} data-testid={`summary-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4 opacity-70" />
        <span className="text-xs font-medium opacity-80">{label}</span>
      </div>
      <div className="text-xl font-bold">{value}</div>
    </div>
  );
}

function TechnicianRow({ tech, rank, expanded, onToggle }) {
  const scoreColor = tech.total_score > 0 ? 'text-green-600' : tech.total_score < 0 ? 'text-red-600' : 'text-zinc-600';
  const rankBg = rank === 1 ? 'bg-yellow-50' : rank === 2 ? 'bg-zinc-50' : rank === 3 ? 'bg-amber-50' : '';

  return (
    <div className={rankBg} data-testid={`tech-perf-row-${tech.technician_id}`}>
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-zinc-50 transition-colors"
        onClick={onToggle}
      >
        {/* Sıra */}
        <div className="w-8 text-center">
          {rank <= 3 ? (
            <Trophy className={`h-5 w-5 mx-auto ${rank === 1 ? 'text-yellow-500' : rank === 2 ? 'text-zinc-400' : 'text-amber-600'}`} />
          ) : (
            <span className="text-sm font-bold text-zinc-400">{rank}</span>
          )}
        </div>

        {/* Ad */}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-zinc-900 truncate">{tech.technician_full_name}</div>
          <div className="text-xs text-zinc-500 truncate">{tech.group_names?.join(', ')}</div>
        </div>

        {/* Statistika Badges */}
        <div className="hidden sm:flex items-center gap-2">
          <StatBadge icon={Wrench} value={tech.tickets_closed} color="indigo" title="Servislər" />
          <StatBadge icon={Phone} value={tech.connections_completed} color="emerald" title="Qoşulmalar" />
          <StatBadge icon={Users} value={tech.d2d_converted} color="orange" title="D2D" />
        </div>

        {/* Xal */}
        <div className="flex items-center gap-1 min-w-[70px] justify-end">
          {tech.total_score > 0 ? (
            <TrendingUp className="h-4 w-4 text-green-600" />
          ) : tech.total_score < 0 ? (
            <TrendingDown className="h-4 w-4 text-red-600" />
          ) : null}
          <span className={`text-lg font-bold font-mono ${scoreColor}`}>
            {tech.total_score > 0 ? '+' : ''}{tech.total_score}
          </span>
        </div>

        {/* Expand */}
        <div className="w-6">
          {expanded ? <ChevronUp className="h-4 w-4 text-zinc-400" /> : <ChevronDown className="h-4 w-4 text-zinc-400" />}
        </div>
      </div>

      {/* Genişlənmiş Detallar */}
      {expanded && (
        <div className="px-4 pb-4 pt-1 grid grid-cols-1 md:grid-cols-3 gap-3 border-t border-zinc-100 bg-zinc-50">
          {/* Servislər */}
          <DetailCard title="Servislər" icon={Wrench} color="indigo" items={[
            { label: 'Bağlanmış', value: tech.tickets_closed, color: 'text-green-600' },
            { label: 'Gözləyən', value: tech.tickets_pending + tech.tickets_assigned, color: 'text-amber-600' },
            { label: 'İcrada', value: tech.tickets_in_progress, color: 'text-blue-600' },
            { label: 'Geri qaytarılmış', value: tech.tickets_rejected, color: 'text-red-600' },
            { label: 'İmtina', value: tech.tickets_declined, color: 'text-red-500' },
            { label: 'Gecikmiş', value: tech.tickets_delayed, color: 'text-yellow-600' },
            { label: 'Ort. vaxt', value: `${tech.tickets_avg_hours} saat`, color: 'text-zinc-700' },
            { label: 'Xal', value: tech.tickets_score > 0 ? `+${tech.tickets_score}` : tech.tickets_score, color: tech.tickets_score >= 0 ? 'text-green-600' : 'text-red-600' },
          ]} />

          {/* Qoşulmalar */}
          <DetailCard title="Qoşulmalar" icon={Phone} color="emerald" items={[
            { label: 'Tamamlanmış', value: tech.connections_completed, color: 'text-green-600' },
            { label: 'Planlanmış', value: tech.connections_scheduled, color: 'text-blue-600' },
            { label: 'İcrada', value: tech.connections_in_progress, color: 'text-blue-500' },
            { label: 'Geri qaytarılmış', value: tech.connections_rejected, color: 'text-red-600' },
            { label: 'Ort. vaxt', value: `${tech.connections_avg_hours} saat`, color: 'text-zinc-700' },
            { label: 'Xal', value: tech.connections_score > 0 ? `+${tech.connections_score}` : tech.connections_score, color: tech.connections_score >= 0 ? 'text-green-600' : 'text-red-600' },
          ]} />

          {/* D2D */}
          <DetailCard title="D2D Qeydləri" icon={Users} color="orange" items={[
            { label: 'Cəm', value: tech.d2d_total, color: 'text-zinc-700' },
            { label: 'Çevrilmiş', value: tech.d2d_converted, color: 'text-green-600' },
            { label: 'Ləğv edilmiş', value: tech.d2d_cancelled, color: 'text-red-600' },
            { label: 'Çevrilmə faizi', value: `${tech.d2d_conversion_rate}%`, color: 'text-blue-600' },
          ]} />
        </div>
      )}
    </div>
  );
}

function StatBadge({ icon: Icon, value, color, title }) {
  if (!value) return null;
  const colorMap = {
    indigo: 'bg-indigo-100 text-indigo-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    orange: 'bg-orange-100 text-orange-700',
  };
  return (
    <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colorMap[color]}`} title={title}>
      <Icon className="h-3 w-3" />
      {value}
    </div>
  );
}

function DetailCard({ title, icon: Icon, color, items }) {
  const headerColors = {
    indigo: 'bg-indigo-600',
    emerald: 'bg-emerald-600',
    orange: 'bg-orange-600',
  };
  return (
    <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
      <div className={`${headerColors[color]} px-3 py-1.5 flex items-center gap-2`}>
        <Icon className="h-3.5 w-3.5 text-white" />
        <span className="text-xs font-bold text-white uppercase">{title}</span>
      </div>
      <div className="p-2 space-y-1">
        {items.map((item, i) => (
          <div key={i} className="flex justify-between items-center text-xs">
            <span className="text-zinc-500">{item.label}</span>
            <span className={`font-semibold ${item.color}`}>{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
